<template>
  <card-dialog
    v-model="modalVisible"
    max-width="650px"
    :title="trainingExerciseFormData.id ? $t('edit_exercise.dialog.header') : $t('training_builder.new_exercise.dialog.header')"
  >
    <v-card-text
      class="pa-6 droparea"
      @dragenter.prevent="dragenter"
    >
      <validation-observer ref="validation">
        <validation-provider
          v-slot="{ errors }"
          rules="required"
        >
          <v-select
            v-model="trainingExerciseFormData.training_exercise_group_id"
            :error-messages="errors"
            :label="$t('training_builder.dropdown.select_group.title')"
            dense
            outlined
            :items="groups.data"
            item-value="id"
            item-text="name"
          />
        </validation-provider>
        <validation-provider
          v-slot="{ errors }"
          rules="required"
        >
          <v-text-field
            v-model="trainingExerciseFormData.name"
            :error-messages="errors"
            :label="$t('training_builder.text_input.exercise_name.title')"
            dense
            outlined
          />
        </validation-provider>
        <validation-provider
          v-slot="{ errors }"
          rules="required"
        >
          <v-textarea
            v-model="trainingExerciseFormData.description"
            :error-messages="errors"
            :label="$t('text_input.description.title')"
            dense
            outlined
            rows="3"
          />
        </validation-provider>
        <v-divider
          v-if="selectedGroup && selectedGroup.params.tags"
          class="mb-6"
        />
        <v-row
          v-if="selectedGroup && selectedGroup.params.tags"
          dense
        >
          <v-col
            v-for="tag in (selectedGroup.params.tags || [])"
            :key="'tag'+tag.id"
            cols="6"
          >
            <v-select
              v-model="trainingExerciseFormData.tags[tag.id]"
              :items="tag.values"
              :label="tag.label"
              dense
              outlined
              clearable
              hide-details
              :multiple="tag.multiple_choice"
            />
          </v-col>
          <v-col cols="6" />
        </v-row>
        <v-divider class="mb-6" />
        <v-card
          v-if="trainingExerciseFormData.drill"
          outlined
          class="mb-4 elevation-0 grey lighten-4"
        >
          <v-row>
            <v-col>
              <v-img
                :src="trainingExerciseFormData.drill.preview"
                class="ma-3"
                width="200"
                contain
              />
            </v-col>
            <v-col>
              <v-row class="flex-column mt-2">
                <v-col>
                  <span class="font-weight-bold">
                    {{ trainingExerciseFormData.drill.name }}
                  </span>
                </v-col>
                <v-col>
                  <div
                    class="caption mb-3"
                    style="-webkit-line-clamp: 4; -webkit-box-orient: vertical; display: -webkit-box; overflow: hidden; width: 100%"
                  >
                    {{ trainingExerciseFormData.drill.description }}
                  </div>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="auto">
              <v-btn
                color="grey darken-2"
                icon
                class="text-none"
                @click="trainingExerciseFormData.drill = null"
              >
                <v-icon
                  small
                >
                  fal fa-xmark
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card>

        <v-row
          v-else-if="selectedGroup && selectedGroup.group_id === 'FTB' && user.organisation_settings && user.organisation_settings.has_drill_module"
          align="center"
          class="mb-4"
        >
          <v-col class="text-body-2 grey--text">
            {{ $t("drill_attach.instruction") }}
          </v-col>
          <v-col cols="auto">
            <v-btn
              color="grey darken-2"
              text
              class="text-none"
              @click="drillModal = true"
            >
              {{ $t("button.attach") }}
              <v-icon
                right
                small
              >
                fal fa-court-sport
              </v-icon>
            </v-btn>
          </v-col>
        </v-row>
        <v-overlay
          v-if="uploadAllowed"
          color="white"
          light
          :value="fileUpload.isDragging"
          :opacity="0.8"
          @dragleave.prevent="dragleave"
          @dragover.prevent="dragover"
          @drop.prevent="drop"
        >
          <div
            class="mb-4 text-center"
            style="pointer-events: none"
          >
            <v-icon
              x-large
              color="grey darken-4"
            >
              fal fa-upload
            </v-icon>
          </div>
          <div
            class="grey--text text--darken-4"
            style="pointer-events: none"
          >
            {{ $t("drag_n_drop.instruction") }}
          </div>
        </v-overlay>

        <v-row
          align="center"
          class="mb-4"
        >
          <v-col class="text-body-2 grey--text">
            {{ $t('media_upload.instruction') }}
          </v-col>
          <v-col cols="auto">
            <v-btn
              color="grey darken-2"
              text
              class="text-none"
              :disabled="!uploadAllowed"
              @click="$refs.uploadFile.click()"
            >
              {{ $t('button.browse') }}
              <v-icon
                right
                small
              >
                fal fa-upload
              </v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <input
          id="upload-file"
          ref="uploadFile"
          type="file"
          style="display: none"
          accept="video/*,audio/*,image/*,.doc,.docx,.pptx,.xlsx,.xls,application/pdf"
          @input="handleFileChange"
        >

        <div>
          <v-row>
            <v-col
              v-for="(mediaItem, mediaItemIndex) in media"
              :key="mediaItem.id"
              cols="6"
            >
              <media-grid-item
                :media-item="mediaItem"
                @preview="preview"
                @delete="mediaItem.id ? startMediaDelete(mediaItem) : media.splice(mediaItemIndex, 1)"
              />
            </v-col>
          </v-row>
        </div>
      </validation-observer>
    </v-card-text>
    <template v-slot:actions>
      <v-card-actions class="pa-4">
        <div class="flex-grow-1">
          <v-btn
            color="primary"
            block
            large
            :loading="submitting"
            :disabled="media.some(m => m.upload)"
            @click="submit()"
          >
            {{ $t("button.save") }}
          </v-btn>
          <v-btn
            v-if="trainingExerciseFormData.id"
            color="error"
            block
            text
            class="mt-2"
            :loading="deleting"
            @click="deleteConfirmationDialogVisible = true"
          >
            {{ $t("button.delete") }}
          </v-btn>
        </div>
      </v-card-actions>
    </template>

    <media-viewer-dialog
      :visible.sync="mediaViewerDialog.visible"
      :media="media"
      :view-media-id="mediaViewerDialog.media ? mediaViewerDialog.media.id : undefined"
    />

    <delete-confirmation-dialog
      :visible.sync="deleteMedia.dialog"
      :submitting="deleteMedia.deleting"
      @confirmed="destroyMedia()"
    />

    <delete-confirmation-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      @confirmed="deleteTrainingExercise()"
    />

    <card-dialog
      v-if="selectedGroup"
      v-model="paramsDialog"
      max-width="800px"
    >
      <template v-slot:title>
        <v-card-title
          class="blue-grey lighten-5 px-6 pt-4 pb-0"
        >
          <div
            class="d-flex flex-column"
            style="width: 100%"
          >
            <div>{{ $t('training_builder.configure_parameters.dialog.header') }} {{ trainingExerciseFormData.name }}</div>
            <div class="mt-4">
              <v-row
                no-gutters
                align="center"
                class="flex-nowrap"
              >
                <v-col style="min-width: 0">
                  <v-tabs
                    v-model="currentSet"
                    height="36px"
                    background-color="transparent"
                  >
                    <v-tab
                      v-for="(setData, setIndex) in trainingExerciseFormData.params.SETS"
                      :key="'tabset'+setIndex"
                      class="text-none text-body-1 px-2"
                    >
                      {{ $t("training_builder.configure.set", {set: setIndex + 1 }) }}
                    </v-tab>
                  </v-tabs>
                </v-col>
                <v-col
                  cols="auto"
                >
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        text
                        color="primary"
                        class="text-none"
                        small
                        @click="duplicateSet(currentSet)"
                        v-on="on"
                      >
                        <v-icon
                          x-small
                        >
                          far fa-copy
                        </v-icon>
                      </v-btn>
                    </template>
                    <span>
                      Duplicate set
                    </span>
                  </v-tooltip>
                </v-col>
                <v-col
                  cols="auto"
                >
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        text
                        color="error"
                        class="text-none"
                        :disabled="currentSet === 0"
                        small
                        @click="deleteSet()"
                        v-on="on"
                      >
                        <v-icon
                          x-small
                        >
                          far fa-trash
                        </v-icon>
                      </v-btn>
                    </template>
                    <span>
                      Delete set
                    </span>
                  </v-tooltip>
                </v-col>
                <v-col
                  cols="auto"
                >
                  <v-btn
                    text
                    color="primary"
                    class="text-none"
                    small
                    @click="addSet()"
                  >
                    <v-icon
                      x-small
                      left
                    >
                      far fa-plus
                    </v-icon>
                    Add set
                  </v-btn>
                </v-col>
              </v-row>
            </div>
          </div>
        </v-card-title>
      </template>

      <v-card-text
        class="pa-0"
      >
        <div
          v-if="trainingExerciseFormData.params.SETS && currentSet < trainingExerciseFormData.params.SETS.length && trainingExerciseFormData.params.SETS[currentSet]"
          class="pa-6"
        >
          <validation-observer ref="validationObserver">
            <ballistics-params-entry
              v-if="selectedGroup.group_id === groupIds.BALLISTICS"
              :set-params.sync="trainingExerciseFormData.params.SETS[currentSet]"
              :exercise="originalTrainingExercise"
            />
            <cardio-params-entry
              v-else-if="selectedGroup.group_id === groupIds.CARDIO"
              :set-params.sync="trainingExerciseFormData.params.SETS[currentSet]"
              :exercise="originalTrainingExercise"
            />
            <endurance-params-entry
              v-else-if="selectedGroup.group_id === groupIds.ENDURANCE"
              :set-params.sync="trainingExerciseFormData.params.SETS[currentSet]"
              :exercise="originalTrainingExercise"
            />
            <football-params-entry
              v-else-if="selectedGroup.group_id === groupIds.FOOTBALL"
              :set-params.sync="trainingExerciseFormData.params.SETS[currentSet]"
              :exercise="originalTrainingExercise"
            />
            <plyometrics-params-entry
              v-else-if="selectedGroup.group_id === groupIds.PLYOMETRICS"
              :set-params.sync="trainingExerciseFormData.params.SETS[currentSet]"
              :exercise="originalTrainingExercise"
            />
            <speed-params-entry
              v-else-if="selectedGroup.group_id === groupIds.SPEED"
              :set-params.sync="trainingExerciseFormData.params.SETS[currentSet]"
              :exercise="originalTrainingExercise"
            />
            <strength-params-entry
              v-else-if="selectedGroup.group_id === groupIds.STRENGTH"
              :set-params.sync="trainingExerciseFormData.params.SETS[currentSet]"
              :exercise="originalTrainingExercise"
            />
            <mobility-params-entry
              v-else-if="selectedGroup.group_id === groupIds.MOBILITY"
              :set-params.sync="trainingExerciseFormData.params.SETS[currentSet]"
              :exercise="originalTrainingExercise"
            />
          </validation-observer>
        </div>
      </v-card-text>

      <v-card-actions class="grey-bg">
        <v-spacer />
        <v-btn
          color="primary"
          @click="paramsDialog = false"
        >
          {{ $t("button.save") }}
        </v-btn>
      </v-card-actions>
    </card-dialog>

    <card-dialog
      v-model="drillModal"
      :title="$t('card_dialog.attach_drill.title')"
      fullscreen
    >
      <v-card-text>
        <drill-list-view
          :attach-mode="true"
          @attach="handleAttach"
        />
      </v-card-text>
    </card-dialog>
  </card-dialog>
</template>

<script>
import CardDialog from '@/components/helpers/CardDialog'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import { EventBus } from '@/plugins/bus'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog'
import { TrainingExerciseApiService } from '@/data/services/TrainingExerciseApiService'
import { cloneDeep, groupBy } from 'lodash'
import _axios from '@/plugins/axios'
import MediaGridItem from '@/components/helpers/MediaGridItem.vue'
import MediaViewerDialog from '@/components/dialogs/MediaViewerDialog.vue'
import { MediaApiService } from '@/data/services/MediaApiService'
import PlyometricsParamsEntry from '@/components/training-builder/plyometrics/PlyometricsParamsEntry.vue'
import StrengthParamsEntry from '@/components/training-builder/strength/StrengthParamsEntry.vue'
import FootballParamsEntry from '@/components/training-builder/football/FootballParamsEntry.vue'
import CardioParamsEntry from '@/components/training-builder/cardio/CardioParamsEntry.vue'
import BallisticsParamsEntry from '@/components/training-builder/ballistics/BallisticsParamsEntry.vue'
import SpeedParamsEntry from '@/components/training-builder/speed/SpeedParamsEntry.vue'
import EnduranceParamsEntry from '@/components/training-builder/endurance/EnduranceParamsEntry.vue'
import { TrainingExerciseGroupId, TrainingExercisesGroups } from '@/data/models/TrainingExercise'
import { cloneVariables } from '@/components/training-builder/helpers'
import MobilityParamsEntry from '@/components/training-builder/mobility/MobilityParamsEntry.vue'
import DrillListView from '@/views/DrillListView.vue'

const emptyTrainingExerciseFormData = {
  id: undefined,
  name: '',
  description: '',
  training_exercise_group_id: null,
  params: {
    SETS: []
  },
  tags: {},
  drill: null
}

export default {
  name: 'TrainingExerciseFormDialog',
  components: {
    DrillListView,
    MobilityParamsEntry,
    EnduranceParamsEntry,
    SpeedParamsEntry,
    BallisticsParamsEntry,
    CardioParamsEntry,
    FootballParamsEntry,
    StrengthParamsEntry,
    PlyometricsParamsEntry,
    MediaViewerDialog,
    MediaGridItem,
    DeleteConfirmationDialog,
    CardDialog
  },
  mixins: [deleteConfirmationMixin],
  props: {
    trainingExercise: {
      type: Object,
      default: undefined
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      trainingExerciseFormData: { ...emptyTrainingExerciseFormData },
      originalTrainingExercise: undefined,
      submitting: false,
      groups: {
        loading: false,
        data: []
      },
      fileUpload: {
        isDragging: false,
        isUploading: false
      },
      media: [],
      mediaViewerDialog: {
        visible: false,
        media: undefined
      },
      deleteMedia: {
        media: undefined,
        dialog: false,
        deleting: false
      },
      paramsDialog: false,
      currentSet: 0,
      drill: null,
      drillModal: false
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    selectedGroup() {
      return this.groups.data.find(g => g.id === this.trainingExerciseFormData.training_exercise_group_id)
    },
    uploadAllowed() {
      return this.media.length < 1
    }
  },
  created() {
    this.groupIds = TrainingExerciseGroupId

    EventBus.$on(this.$events.OPEN_DIALOG_TRAINING_EXERCISE_FORM, () => {
      this.modalVisible = true
      this.trainingExerciseFormData = { ...emptyTrainingExerciseFormData, tags: {} }
      this.media = []
      this.originalTrainingExercise = undefined

      this.$nextTick(() => {
        this.$refs.validation.reset()
      })
    })

    EventBus.$on(this.$events.EDIT_TRAINING_EXERCISE, (trainingExercise) => {
      this.originalTrainingExercise = cloneDeep(trainingExercise)

      this.trainingExerciseFormData = {
        id: trainingExercise.id,
        name: trainingExercise.name,
        description: trainingExercise.description,
        training_exercise_group_id: trainingExercise.group.id,
        tags: {},
        drill: trainingExercise.drill,
        params: trainingExercise.params || {
          SETS: []
        }
      }

      this.$nextTick(() => {
        this.trainingExerciseFormData.tags = this.combineTags(trainingExercise.tags)
      })

      if (trainingExercise.media && trainingExercise.media.length > 0) {
        this.media = trainingExercise.media.map(m => ({ ...m }))
      } else {
        this.media = []
      }

      this.modalVisible = true
    })

    this.fetchAllGroups()
  },
  methods: {
    async submit() {
      if (!(await this.$refs.validation.validate())) return

      await this.save()
    },
    async save() {
      try {
        this.submitting = true

        const data = {
          name: this.trainingExerciseFormData.name,
          description: this.trainingExerciseFormData.description,
          training_exercise_group_id: this.trainingExerciseFormData.training_exercise_group_id,
          tags: this.trainingExerciseFormData.tags,
          media_ids: this.media.map(m => m.id),
          drill_id: this.trainingExerciseFormData.drill ? this.trainingExerciseFormData.drill.id : null
        }

        let result

        if (this.trainingExerciseFormData.id) {
          result = await TrainingExerciseApiService.updateUserExercise(this.trainingExerciseFormData.id, data)
        } else {
          result = await TrainingExerciseApiService.createUserExercise(data)
        }

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.exercise_save.success'),
          color: 'success'
        })

        this.$emit('refresh', result.data)

        if (this.trainingExerciseFormData.id) {
          EventBus.$emit(this.$events.TRAINING_EXERCISE_UPDATED, result.data)
        } else {
          EventBus.$emit(this.$events.TRAINING_EXERCISE_CREATED, result.data)
        }

        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.exercise_save.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async deleteTrainingExercise() {
      try {
        this.deleting = true

        await TrainingExerciseApiService.deleteUserExercise(this.trainingExerciseFormData.id)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.exercise_delete.success'),
          color: 'success'
        })

        this.deleteConfirmationDialogVisible = false

        EventBus.$emit(this.$events.TRAINING_EXERCISE_DELETED, this.trainingExerciseFormData)

        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.exercise_delete.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.deleting = false
      }
    },
    async fetchAllGroups() {
      this.groups.loading = true

      try {
        const result = await TrainingExerciseApiService.getAllGroups()

        this.groups.data = result
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_groups.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.groups.loading = false
      }
    },
    combineTags(tags) {
      const group = groupBy(tags, 'tag_id')

      const object = {}

      for (const key of Object.keys(group)) {
        const groupTagDefinition = this.selectedGroup.params.tags.find(t => t.id === key)

        object[key] = group[key].length === 0 ? null : (groupTagDefinition.multiple_choice ? group[key].map(t => t.value) : group[key][0].value)
      }

      return object
    },
    async handleFileChange() {
      await this.$nextTick()

      const files = [...this.$refs.uploadFile.files]

      const allowedExtensions = []

      for (const file of files) {
        const type = file.type
        const extension = file.name.split('.').pop()

        if (type.match('image.*') || type.match('video.*') || type.match('audio.*') || allowedExtensions.indexOf(extension) > -1) {
          this.media.unshift({
            file: file,
            name: file.name,
            progress: 0,
            mime_type: file.type,
            size: file.size,
            upload: true
          })
        } else {
          this.media.unshift({
            file: file,
            name: file.name,
            progress: 0,
            mime_type: file.type,
            size: file.size,
            error: 'File type not allowed'
          })
        }
      }

      this.$refs.uploadFile.value = null

      this.processUpload()
    },
    preview(media) {
      this.mediaViewerDialog.visible = true
      this.mediaViewerDialog.media = media
    },
    dragover(e) {
      this.fileUpload.isDragging = true
    },
    dragleave(e) {
      this.fileUpload.isDragging = false
    },
    dragenter(e) {
      this.fileUpload.isDragging = true
    },
    drop(e) {
      this.$refs.uploadFile.files = e.dataTransfer.files
      this.handleFileChange()
      this.fileUpload.isDragging = false
    },
    startMediaDelete(media) {
      this.deleteMedia.media = media
      this.deleteMedia.dialog = true
    },
    async processUpload() {
      let formData

      for (const file of this.media.filter(m => !!m.upload)) {
        formData = new FormData()
        formData.append('file', file.file, file.name)

        _axios.post('training-exercises/media-upload', formData, {
          onUploadProgress: (e) => {
            file.progress = Number((e.loaded / e.total) * 100)
          }
        }).then(response => {
          file.upload = false

          Object.assign(file, response.data.media)
        }).catch(error => {
          if (error.response && error.response.status === 422) {
            this.$store.dispatch(this.$storeActions.ALERT, {
              color: 'error',
              text: this.$t('alert.file_type_not_allowed.error')
            })

            file.error = this.$t('alert.file_type_not_allowed.error')

            return
          }

          this.$store.dispatch(this.$storeActions.ALERT, {
            color: 'error',
            text: this.$t('alert.uploading_file.error')
          })

          file.error = this.$t('alert.uploading_file.error')
        })
      }
    },
    async destroyMedia() {
      if (!this.deleteMedia.media) return

      const id = this.deleteMedia.media.id

      try {
        this.deleteMedia.deleting = true

        await MediaApiService.delete(id)

        const index = this.media.findIndex(m => m.id === id)

        if (index > -1) this.media.splice(index, 1)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.media_delete.success'),
          color: 'success'
        })

        this.deleteMedia.dialog = false

        EventBus.$emit(this.$events.TRAINING_EXERCISE_UPDATED, this.trainingExerciseFormData)
      } catch (e) {

      } finally {
        this.deleteMedia.media = undefined
        this.deleteMedia.deleting = false
      }
    },
    addSet() {
      this.trainingExerciseFormData.params.SETS.push(cloneVariables(TrainingExercisesGroups[this.selectedGroup.group_id].params))
    },
    duplicateSet(setNumber) {
      if (!this.trainingExerciseFormData.params.SETS[setNumber]) return

      this.trainingExerciseFormData.params.SETS.push({ ...this.trainingExerciseFormData.params.SETS[setNumber] })
    },
    deleteSet() {
      this.trainingExerciseFormData.params.SETS.splice(this.currentSet, 1)
    },
    initEditDefaultParameters() {
      if (this.trainingExerciseFormData.params.SETS.length === 0) {
        this.trainingExerciseFormData.params.SETS.push(cloneVariables(TrainingExercisesGroups[this.selectedGroup.group_id].params))
      }

      this.paramsDialog = true
    },
    handleAttach(drill) {
      this.drill = drill
      this.drillModal = false

      this.trainingExerciseFormData.drill = drill
      this.trainingExerciseFormData.name = drill.name
      this.trainingExerciseFormData.description = drill.description
      for (const tag of drill.tags) {
        this.trainingExerciseFormData.tags[tag.tag_id] = tag.value
      }
    }
  }
}
</script>
